import { getTaskById } from '../../../taskRefactored/tasks.service'
import {
    getStoryMapCard,
    getStoryMapDetail,
    isMongoObjectId,
} from '../../storyMap.service'

const APP_URL_CANDIDATE_RE =
    /^(?:https?:\/\/[^\s]+|\/dashboard\/projects\/[^\s]+)$/i

const looksLikeSingleUrl = (text = '') => {
    const trimmed = String(text).trim()
    if (!trimmed || /\s/.test(trimmed)) return false
    return APP_URL_CANDIDATE_RE.test(trimmed)
}

/**
 * Parse an app Task or Story Map deep link into a resolve target.
 * @returns {null | { type: 'task', taskId: string, href: string } | { type: 'storyMap', cardType: 'story'|'step', cardId: string, href: string } | { type: 'storyMapBoard', storyMapId: string, href: string }}
 */
export const parseAppDeepLink = (raw = '') => {
    const trimmed = String(raw).trim()
    if (!trimmed) return null

    let url
    try {
        url = new URL(trimmed, window.location.origin)
    } catch {
        return null
    }

    const path = url.pathname.replace(/\/+$/, '') || '/'

    const storyMapMatch = path.match(
        /^\/dashboard\/projects\/([^/]+)\/storyMap$/
    )
    if (storyMapMatch) {
        const storyMapId = url.searchParams.get('storyMapId')
        const storyId = url.searchParams.get('storyId')
        const stepId = url.searchParams.get('stepId')

        if (isMongoObjectId(storyMapId) && isMongoObjectId(storyId)) {
            return {
                type: 'storyMap',
                cardType: 'story',
                cardId: String(storyId),
                href: url.href,
            }
        }
        if (isMongoObjectId(storyMapId) && isMongoObjectId(stepId)) {
            return {
                type: 'storyMap',
                cardType: 'step',
                cardId: String(stepId),
                href: url.href,
            }
        }
        if (isMongoObjectId(storyMapId)) {
            return {
                type: 'storyMapBoard',
                storyMapId: String(storyMapId),
                href: url.href,
            }
        }
        return null
    }

    const taskMatch = path.match(
        /^\/dashboard\/projects\/([^/]+)\/([^/]+)\/([^/]+)$/
    )
    if (taskMatch) {
        const [, , view, taskId] = taskMatch
        if (view === 'storyMap') return null
        if (!isMongoObjectId(taskId)) return null
        return {
            type: 'task',
            taskId: String(taskId),
            href: url.href,
        }
    }

    return null
}

const pickTaskTitle = (payload) => {
    const task =
        payload?.data?.data ||
        payload?.data?.result ||
        payload?.data ||
        payload?.result ||
        payload
    const title = task?.title || task?.name
    return typeof title === 'string' ? title.trim() : ''
}

const pickCardTitle = (detail, cardType) => {
    const name = detail?.name || detail?.title
    if (typeof name === 'string' && name.trim()) return name.trim()
    return cardType === 'step' ? 'Untitled step' : 'Untitled story'
}

const pickStoryMapBoardTitle = (board) => {
    const name = board?.title || board?.name
    if (typeof name === 'string' && name.trim()) return name.trim()
    return 'Untitled Story Map'
}

const KIND_LABEL = {
    task: 'Task',
    story: 'Story',
    step: 'Step',
    storyMap: 'Story Map',
}

/** Visible linked text — title only (no icons / prefixes). */
export const formatAppLinkLabel = (kind, title) => {
    const name = String(title || '').trim()
    if (name) return name
    if (kind === 'step') return 'Untitled step'
    if (kind === 'story') return 'Untitled story'
    if (kind === 'task') return 'Untitled task'
    if (kind === 'storyMap') return 'Untitled Story Map'
    return ''
}

/**
 * Hover tooltip detail, e.g. "Story: Verification… · Journey › Step"
 * @param {'task'|'story'|'step'|'storyMap'|string} kind
 * @param {string} title
 * @param {string[]} [crumbs]
 */
export const formatAppLinkTooltip = (kind, title, crumbs = []) => {
    const kindLabel = KIND_LABEL[kind] || ''
    const name = String(title || '').trim()
    const crumbLine = (Array.isArray(crumbs) ? crumbs : [])
        .map((c) => String(c || '').trim())
        .filter(Boolean)
        .join(' › ')

    const head =
        kindLabel && name
            ? `${kindLabel}: ${name}`
            : name || kindLabel || ''
    if (!head) return crumbLine
    if (!crumbLine) return head
    return `${head} · ${crumbLine}`
}

/** Quill formats bag: link href as string + optional title attr for hover. */
export const toAppLinkFormats = (href, tooltip) => {
    const url = getLinkHref(href)
    const tip = String(tooltip || '').trim()
    if (!url) return {}
    if (!tip) return { link: url }
    return { link: url, appLinkTitle: tip }
}

/** Normalize Quill link format (string or { href, title }) to href string. */
export const getLinkHref = (linkValue) => {
    if (linkValue == null || linkValue === false) return ''
    if (typeof linkValue === 'string') {
        const trimmed = linkValue.trim()
        if (!trimmed || trimmed === '[object Object]') return ''
        return trimmed
    }
    if (typeof linkValue === 'object') {
        // Unwrap nested { href } / accidental double-wrap from older object format
        if (linkValue.href != null && linkValue.href !== linkValue) {
            return getLinkHref(linkValue.href)
        }
    }
    return ''
}

export const linksShareHref = (a, b) => {
    const left = getLinkHref(a)
    const right = getLinkHref(b)
    return Boolean(left && right && left === right)
}

/**
 * If clipboard text is a single Task/Story Map app link, fetch its title.
 * @returns {Promise<null | { href: string, title: string, kind: 'task'|'story'|'step'|'storyMap' }>}
 */
export const resolveAppLinkTitle = async (rawText) => {
    if (!looksLikeSingleUrl(rawText)) return null

    const parsed = parseAppDeepLink(rawText)
    if (!parsed) return null

    try {
        if (parsed.type === 'task') {
            const response = await getTaskById({ _id: parsed.taskId })
            const title = pickTaskTitle(response?.data ?? response)
            if (!title) return null
            return {
                href: parsed.href,
                kind: 'task',
                title: formatAppLinkLabel('task', title),
            }
        }

        if (parsed.type === 'storyMapBoard') {
            const result = await getStoryMapDetail(parsed.storyMapId)
            if (!result?.success || !result?.board) return null
            const title = pickStoryMapBoardTitle(result.board)
            return {
                href: parsed.href,
                kind: 'storyMap',
                title: formatAppLinkLabel('storyMap', title),
            }
        }

        const module = parsed.cardType === 'story' ? 'story' : 'step'
        const result = await getStoryMapCard(parsed.cardId, module)
        if (!result?.success || !result?.detail) return null
        const title = pickCardTitle(result.detail, parsed.cardType)
        if (!title) return null
        return {
            href: parsed.href,
            kind: parsed.cardType,
            title: formatAppLinkLabel(parsed.cardType, title),
        }
    } catch {
        return null
    }
}

/**
 * True when pasted plain text should be treated as an app deep link paste.
 */
export const shouldResolveAppLinkPaste = (text) =>
    Boolean(parseAppDeepLink(String(text || '').trim()))
